import { useEffect } from 'react';
import { useAuthStore } from '../../store/useAuthStore';
import { useAppStore } from '../../store/useAppStore';
import { clearSupabaseAuthSession, supabase, isAuthConfigured } from '../../lib/supabase';
import { Spinner } from '../common/Spinner';

/**
 * Mounts once at the top of the tree.
 * Restores the persisted session from browser storage and subscribes to auth
 * state changes (sign-in, sign-out, token refresh, password recovery).
 * The subscription is cleaned up when the component unmounts.
 *
 * When Supabase env vars are not configured (bootstrap/dev/CI mode),
 * auth is skipped entirely and the app renders immediately.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const setSession = useAuthStore((s) => s.setSession);
  const setLoading = useAuthStore((s) => s.setLoading);
  const isLoading = useAuthStore((s) => s.isLoading);

  useEffect(() => {
    if (!isAuthConfigured) {
      // Bootstrap mode: mark as loaded with no session (protected routes
      // will render anyway because isAuthConfigured is also checked there).
      setLoading(false);
      return;
    }

    // Restore existing session from storage. If storage is corrupt or a
    // refresh token has already been revoked, clear the browser copy and render
    // the unauthenticated state rather than leaving the app on a spinner.
    supabase.auth
      .getSession()
      .then(({ data: { session }, error }) => {
        const activeSession = useAuthStore.getState().session;
        if (error) {
          if (!activeSession) {
            clearSupabaseAuthSession();
            setSession(null);
          }
        } else if (session || !activeSession) {
          setSession(session);
        }
      })
      .catch(() => {
        if (!useAuthStore.getState().session) {
          clearSupabaseAuthSession();
          setSession(null);
        }
      })
      .finally(() => setLoading(false));

    // Subscribe to auth state changes and clean up on unmount
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      const currentSession = useAuthStore.getState().session;
      if (event === 'INITIAL_SESSION' && !session && currentSession) {
        return;
      }

      // Synchronously wipe user-specific app data whenever the active user
      // changes. This must happen before setSession() triggers a re-render so
      // that no stale data is ever visible to the incoming user.
      const prevUserId = currentSession?.user?.id;
      const nextUserId = session?.user?.id;
      if (event === 'SIGNED_OUT' || (event === 'SIGNED_IN' && nextUserId !== prevUserId)) {
        useAppStore.getState().resetUserData();
      }
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, [setSession, setLoading]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-surface-0">
        <Spinner size="lg" />
      </div>
    );
  }

  return <>{children}</>;
}
